// src/components/editor/CitationModal.jsx
//
// Citation manager for the Manuscript Editor Tool tab.
// Sources are persisted per-draft (backend/routes/citations.py via citationApi);
// formatting is done client-side (src/utils/citationFormat.js). Insertion is
// delegated to the parent via props so caret/selection + pagination logic stays
// in EditorPage:
//   • onInsert(html)              — drop an in-text citation at the caret
//   • onInsertBibliography(html)  — insert OR replace the document bibliography
//   • onRestyle(mapIdToText)      — reformat existing in-text citations in place
// editorRef (read-only) lets us see which sources are actually cited so the
// bibliography can be limited to those.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, IconButton, TextField, MenuItem,
  List, ListItem, ListItemText, Divider, CircularProgress, Alert, Chip,
  Checkbox, FormControlLabel, InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import SearchIcon from '@mui/icons-material/Search';
import StyleIcon from '@mui/icons-material/Style';
import { citationApi } from '../../utils/readerApi';
import { formatInText, formatBibliographyEntry, CITATION_STYLES } from '../../utils/citationFormat';

const SOURCE_TYPES = [
  { value: 'book', label: 'Book' },
  { value: 'journal', label: 'Journal article' },
  { value: 'website', label: 'Website' },
  { value: 'other', label: 'Other' },
];

const EMPTY = {
  type: 'book', title: '', authors: '', year: '',
  publisher: '', publisherPlace: '', journal: '', volume: '', issue: '',
  pages: '', url: '', doi: '', containerTitle: '', accessed: '',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#F5D76E' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#F5D76E' },
  '& .MuiInputBase-input': { colorScheme: 'dark' },
};

const CitationModal = ({
  open, onClose, draftId, editorRef,
  onInsert, onInsertBibliography, onRestyle,
}) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [style, setStyle] = useState('apa');
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  // Phase 2 — DOI/ISBN auto-import
  const [lookupQuery, setLookupQuery] = useState('');
  const [lookingUp, setLookingUp] = useState(false);

  // Phase 3 — bibliography options + per-source in-text page locators
  const [onlyCited, setOnlyCited] = useState(false);
  const [locators, setLocators] = useState({}); // { [sourceId]: 'page(s)' }

  const load = useCallback(async () => {
    if (!draftId) return;
    setLoading(true);
    setError('');
    try {
      const data = await citationApi.list(draftId);
      setSources(Array.isArray(data?.citations) ? data.citations : []);
    } catch (e) {
      setError(e?.message || 'Failed to load citations.');
    } finally {
      setLoading(false);
    }
  }, [draftId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const resetForm = () => { setForm(EMPTY); setEditingId(null); };

  // postJSON throws with the raw response body — unwrap {"error":"..."} for display.
  const humanError = (e, fallback) => {
    let msg = e?.message || fallback;
    try { const p = JSON.parse(msg); if (p?.error) msg = p.error; } catch { /* not JSON */ }
    return msg;
  };

  const handleLookup = async () => {
    if (!lookupQuery.trim()) return;
    setLookingUp(true);
    setError('');
    try {
      const { source } = await citationApi.lookup(lookupQuery.trim());
      setForm({
        ...EMPTY,
        ...source,
        authors: Array.isArray(source.authors) ? source.authors.join('; ') : (source.authors || ''),
      });
      setEditingId(null);
      setLookupQuery('');
    } catch (e) {
      setError(humanError(e, 'Lookup failed.'));
    } finally {
      setLookingUp(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setError('');
    try {
      if (editingId) {
        await citationApi.update(draftId, editingId, form);
      } else {
        await citationApi.create(draftId, form);
      }
      resetForm();
      load();
    } catch (e) {
      setError(humanError(e, 'Failed to save source.'));
    }
  };

  const handleEdit = (src) => {
    setEditingId(src._id);
    setForm({
      ...EMPTY,
      type: src.type || 'book',
      title: src.title || '',
      authors: Array.isArray(src.authors) ? src.authors.join('; ') : (src.authors || ''),
      year: src.year || '',
      publisher: src.publisher || '',
      publisherPlace: src.publisherPlace || '',
      journal: src.journal || '',
      volume: src.volume || '',
      issue: src.issue || '',
      pages: src.pages || '',
      url: src.url || '',
      doi: src.doi || '',
      containerTitle: src.containerTitle || '',
      accessed: src.accessed || '',
    });
  };

  const handleDelete = async (id) => {
    try {
      await citationApi.remove(draftId, id);
      if (editingId === id) resetForm();
      load();
    } catch (e) {
      setError(humanError(e, 'Failed to delete source.'));
    }
  };

  // In-text citation at the caret, honouring an optional per-source page locator.
  const insertInText = (src) => {
    const loc = (locators[src._id] || '').trim();
    const text = formatInText(src, style, loc);
    onInsert?.(`<span class="rv-citation" data-citation-id="${src._id}">${text}</span>&nbsp;`);
  };

  // Which source ids are actually cited in the document (null = can't tell).
  const getCitedIds = () => {
    const el = editorRef?.current;
    if (!el) return null;
    const set = new Set();
    el.querySelectorAll('.rv-citation[data-citation-id]').forEach((n) =>
      set.add(String(n.getAttribute('data-citation-id'))));
    return set;
  };

  // Insert or replace the document's bibliography / works-cited list.
  const insertBibliography = () => {
    let list = sources;
    if (onlyCited) {
      const cited = getCitedIds();
      if (cited) list = sources.filter((s) => cited.has(String(s._id)));
    }
    if (!list.length) {
      setError(onlyCited ? 'No cited sources found in the document.' : 'No sources to add.');
      return;
    }
    setError('');
    const heading = style === 'mla' ? 'Works Cited' : style === 'chicago' ? 'Bibliography' : 'References';
    const items = [...list]
      .sort((a, b) => formatBibliographyEntry(a, style).localeCompare(formatBibliographyEntry(b, style)))
      .map((s) => `<p class="rv-bib-entry" data-rv-bib="1">${formatBibliographyEntry(s, style)}</p>`)
      .join('');
    const html = `<h2 class="rv-bib-heading" data-rv-bib="1">${heading}</h2>${items}`;
    if (onInsertBibliography) onInsertBibliography(html);
    else onInsert?.(html);
  };

  // Reformat every in-text citation already in the document to the current style.
  const applyStyleToDoc = () => {
    if (!onRestyle) return;
    const map = {};
    sources.forEach((s) => { map[String(s._id)] = formatInText(s, style); });
    onRestyle(map);
  };

  const normalizedForPreview = (f) => ({
    ...f,
    authors: (f.authors || '').split(';').map((a) => a.trim()).filter(Boolean),
  });

  const t = form.type;
  const isJournal = t === 'journal';
  const isWebsite = t === 'website';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { bgcolor: '#2d2d44', color: 'white', borderRadius: 2 } }}
    >
      <DialogTitle sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)', pb: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MenuBookIcon sx={{ color: '#F5D76E' }} />
          <Typography variant="h6">Citations</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            select size="small" label="Style" value={style}
            onChange={(e) => setStyle(e.target.value)}
            sx={{ minWidth: 130, ...fieldSx }}
          >
            {CITATION_STYLES.map((s) => (
              <MenuItem key={s} value={s}>{s.toUpperCase()}</MenuItem>
            ))}
          </TextField>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Auto-import by DOI / ISBN */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            size="small" fullWidth label="Auto-fill from DOI or ISBN"
            placeholder="10.1000/xyz123  ·  9780306406157"
            value={lookupQuery}
            onChange={(e) => setLookupQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleLookup(); } }}
            sx={fieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined" onClick={handleLookup} disabled={lookingUp || !lookupQuery.trim()}
            sx={{ color: '#F5D76E', borderColor: 'rgba(245,215,110,0.5)', whiteSpace: 'nowrap',
              '&:hover': { borderColor: '#F5D76E' } }}
          >
            {lookingUp ? <CircularProgress size={20} sx={{ color: '#F5D76E' }} /> : 'Look up'}
          </Button>
        </Box>

        {/* Add / edit form */}
        <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2, p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: '#F5D76E', mb: 1.5 }}>
            {editingId ? 'Edit source' : 'Add source'}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField select size="small" label="Type" value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })} sx={fieldSx}>
              {SOURCE_TYPES.map((ty) => <MenuItem key={ty.value} value={ty.value}>{ty.label}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Year" value={form.year}
              onChange={(e) => setForm({ ...form, year: e.target.value })} sx={fieldSx} />
            <TextField size="small" label="Title" value={form.title} required
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              sx={{ gridColumn: '1 / -1', ...fieldSx }} />
            <TextField size="small" label="Authors (separate with ;)" value={form.authors}
              onChange={(e) => setForm({ ...form, authors: e.target.value })}
              placeholder="Smith, Jane; Doe, John"
              sx={{ gridColumn: '1 / -1', ...fieldSx }} />

            {isJournal && (
              <>
                <TextField size="small" label="Journal" value={form.journal}
                  onChange={(e) => setForm({ ...form, journal: e.target.value })} sx={fieldSx} />
                <TextField size="small" label="Pages" value={form.pages}
                  onChange={(e) => setForm({ ...form, pages: e.target.value })} sx={fieldSx} />
                <TextField size="small" label="Volume" value={form.volume}
                  onChange={(e) => setForm({ ...form, volume: e.target.value })} sx={fieldSx} />
                <TextField size="small" label="Issue" value={form.issue}
                  onChange={(e) => setForm({ ...form, issue: e.target.value })} sx={fieldSx} />
                <TextField size="small" label="DOI" value={form.doi}
                  onChange={(e) => setForm({ ...form, doi: e.target.value })}
                  placeholder="10.1000/xyz123"
                  sx={{ gridColumn: '1 / -1', ...fieldSx }} />
              </>
            )}

            {isWebsite && (
              <>
                <TextField size="small" label="Site name" value={form.containerTitle}
                  onChange={(e) => setForm({ ...form, containerTitle: e.target.value })} sx={fieldSx} />
                <TextField size="small" type="date" label="Accessed" value={form.accessed}
                  onChange={(e) => setForm({ ...form, accessed: e.target.value })}
                  InputLabelProps={{ shrink: true }} sx={fieldSx} />
              </>
            )}

            {!isJournal && !isWebsite && (
              <>
                <TextField size="small" label="Publisher" value={form.publisher}
                  onChange={(e) => setForm({ ...form, publisher: e.target.value })} sx={fieldSx} />
                <TextField size="small" label="Publisher city" value={form.publisherPlace}
                  onChange={(e) => setForm({ ...form, publisherPlace: e.target.value })}
                  placeholder="New York" sx={fieldSx} />
              </>
            )}

            <TextField size="small" label="URL" value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              sx={{ gridColumn: '1 / -1', ...fieldSx }} />
          </Box>

          {form.title.trim() && (
            <Typography variant="caption"
              sx={{ display: 'block', color: 'rgba(255,255,255,0.6)', mt: 1.5, fontStyle: 'italic' }}
              dangerouslySetInnerHTML={{ __html: `Preview: ${formatBibliographyEntry(normalizedForPreview(form), style)}` }}
            />
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleSave}
              sx={{ bgcolor: '#5a4d99', '&:hover': { bgcolor: '#6a5da9' } }}>
              {editingId ? 'Save changes' : 'Add source'}
            </Button>
            {editingId && (
              <Button onClick={resetForm} sx={{ color: 'rgba(255,255,255,0.7)' }}>Cancel</Button>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

        {/* Source list header + bibliography controls */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ color: '#F5D76E' }}>
            Sources ({sources.length})
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={onlyCited}
                onChange={(e) => setOnlyCited(e.target.checked)}
                sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: '#F5D76E' } }} />}
              label={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Only cited</Typography>}
              sx={{ mr: 0.5 }}
            />
            {onRestyle && (
              <Button size="small" startIcon={<StyleIcon />} onClick={applyStyleToDoc}
                sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Apply {style.toUpperCase()} to doc
              </Button>
            )}
            <Button size="small" startIcon={<MenuBookIcon />} onClick={insertBibliography}
              disabled={!sources.length} sx={{ color: '#F5D76E' }}>
              Insert / update bibliography
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress sx={{ color: '#F5D76E' }} />
          </Box>
        ) : sources.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', py: 2, textAlign: 'center' }}>
            No sources yet. Add one above.
          </Typography>
        ) : (
          <List sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
            {sources.map((src) => (
              <ListItem key={src._id} sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <ListItemText
                  primary={<span dangerouslySetInnerHTML={{ __html: formatBibliographyEntry(src, style) }} />}
                  secondary={<Chip label={`in-text: ${formatInText(src, style, (locators[src._id] || '').trim())}`} size="small"
                    sx={{ mt: 0.5, bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)' }} />}
                  primaryTypographyProps={{ sx: { color: 'white' } }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                  <TextField
                    size="small" placeholder="p." value={locators[src._id] || ''}
                    onChange={(e) => setLocators({ ...locators, [src._id]: e.target.value })}
                    title="Optional page/section for in-text citation"
                    sx={{ width: 64, ...fieldSx }}
                  />
                  <IconButton size="small" title="Insert in-text citation"
                    onClick={() => insertInText(src)} sx={{ color: '#4caf50' }}>
                    <FormatQuoteIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" title="Edit"
                    onClick={() => handleEdit(src)} sx={{ color: '#F5D76E' }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" title="Delete"
                    onClick={() => handleDelete(src._id)} sx={{ color: '#f44336' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', p: 2 }}>
        <Button variant="contained" onClick={onClose}
          sx={{ bgcolor: '#F5D76E', color: '#2d2d44', '&:hover': { bgcolor: '#e6c757' } }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CitationModal;
